import React from 'react';
import { Search, PenTool, Rocket, TrendingUp } from 'lucide-react';

const OnboardingJourney = () => {
  const steps = [
    {
      number: 1,
      icon: Search,
      title: 'Discover',
      description: 'We interview key stakeholders and understand your workflows.',
      inputs: 'Goals, org structure, current hiring/training process, performance gaps',
      color: 'blue'
    },
    {
      number: 2,
      icon: PenTool,
      title: 'Design',
      description: 'We craft a custom plan for Recruiting, Training, and Role Play.',
      outputs: 'Tailored scenarios, training paths, scorecards, and automation rules',
      color: 'indigo'
    },
    {
      number: 3,
      icon: Rocket,
      title: 'Deploy',
      description: 'We configure Traain to your environment and integrate where needed.',
      includes: 'User setup, content import, ATS/LMS/CRM integration if applicable',
      color: 'purple'
    },
    {
      number: 4,
      icon: TrendingUp,
      title: 'Elevate',
      description: 'We run your first cycles with you and tune for results.',
      think: 'Calibration, adoption coaching, usage optimization, KPI reviews',
      color: 'green'
    }
  ];

  const colorClasses = {
    blue: 'from-blue-900/30 to-blue-800/30 border-blue-700',
    indigo: 'from-indigo-900/30 to-indigo-800/30 border-indigo-700',
    purple: 'from-purple-900/30 to-purple-800/30 border-purple-700',
    green: 'from-green-900/30 to-green-800/30 border-green-700'
  };

  return (
    <section className="py-20 px-6 bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-4">
            The Onboarding Journey
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Four phases designed to understand, customize, launch, and optimize your success system.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${colorClasses[step.color]} rounded-2xl p-6 border relative group hover:scale-105 transition-transform`}
            >
              {/* Step Number Badge */}
              <div className="absolute -top-3 -left-3 w-12 h-12 bg-gray-950 border-2 border-blue-600 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.4)]">
                <span className="text-xl font-bold text-blue-400">{step.number}</span>
              </div>

              {/* Icon */}
              <div className="mt-6 mb-4">
                <div className="w-14 h-14 bg-gray-900/50 border border-gray-700 rounded-xl flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-blue-400" />
                </div>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-white mb-3">{step.title}</h3>
              <p className="text-gray-300 mb-4 text-sm">{step.description}</p>

              {/* Deliverable */}
              <div className="bg-gray-950/50 rounded-lg p-3 border border-gray-800">
                <p className="text-xs font-semibold text-blue-400 mb-1">
                  {step.inputs && 'Inputs:'}
                  {step.outputs && 'Outputs:'}
                  {step.includes && 'Includes:'}
                  {step.think && 'Think:'}
                </p>
                <p className="text-xs text-gray-400">
                  {step.inputs || step.outputs || step.includes || step.think}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OnboardingJourney;